/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import React, { useMemo, useState } from 'react';

import { ApiDateType } from 'client';
import * as dto from 'client/dto';
import { HistoryPanelFilterOptions, HistoryPanelFilterOptionsValue } from 'models/FilterOptions';
import { sxtheme } from 'utils/cssHelpers';
import { isMobile } from 'utils/responsive';
import { valueof } from 'utils/typeHelpers';

import { Typography } from 'components/_common/Typography';
import BdButton from 'components/_controls/BdButton/BdButton';
import { PanelDropdown } from 'components/_controls/PanelDropdown';
import DashboardToolbar from 'components/Navigationbar/DashboardToolbar';
import { IPanelProps, Panel } from 'components/Panel';
import { ActivePanel } from 'components/SidebarView/sidebar-types';

import { ActivityBlurb } from './ActivityBlurb';
import { usePaginatedActivityHistory } from './PaginatedActivityHistory';

type DiseaseActivityHistoryPanelProps = IPanelProps & {
  activePanel: ActivePanel;
  isWorldwide?: boolean;
  disactIds: dto.CountryDiseaseActivityKey;
  activityAnchorDate?: ApiDateType;
  event: dto.GetEventModel;
};

export const DiseaseActivityHistoryPanel: React.FC<DiseaseActivityHistoryPanelProps> = ({
  activePanel,
  isWorldwide = false,
  disactIds,
  activityAnchorDate,
  event,
  onClose,
  onMinimize,
  isMinimized,
}) => {
  const [filterBy, setFilterBy] = useState<HistoryPanelFilterOptionsValue>(
    valueof<HistoryPanelFilterOptionsValue>('all-activity')
  );
  const filterOptions = useMemo(() => HistoryPanelFilterOptions, []);

  const {
    data,
    activeIndex,
    scrollToIndex,
    isLoadingInitial,
    isLoadingBelow,
    isLoadingAbove,
    loadBelow,
    loadAbove,
  } = usePaginatedActivityHistory(disactIds, activityAnchorDate, filterBy === 'with-updates-only');

  const isMobileDevice = isMobile(useBreakpointIndex());
  const countryName = event?.eventInformation?.countryName;
  const panelTitle = isWorldwide
    ? 'Global Activity'
    : countryName
    ? `Activity in ${countryName}`
    : 'Activity History';

  if (isMobileDevice && activePanel !== 'HistoryPanel') {
    return null;
  }

  return (
    <Panel
      isAnimated
      title={panelTitle}
      grayBG
      isMinimized={isMinimized}
      onClose={onClose}
      onMinimize={onMinimize}
      isLoading={isLoadingInitial}
      scrollToTopButtonThresh={data.hasNewerEntries ? undefined : 600}
      // subtitleMobile={appState.locationFullName || 'Loading...'}
      toolbar={
        <>
          {isMobileDevice && <DashboardToolbar showToolbar />}
          <PanelDropdown
            prefixLabel="Show"
            selectedValue={filterBy}
            options={filterOptions}
            onSelect={val => setFilterBy(val as HistoryPanelFilterOptionsValue)}
            disabled={isLoadingInitial}
          />
          {/* TODO: catherine said to hide for now */}
          {/* <BdSearch
            searchText={searchFilterText}
            placeholder="Search for country"
            debounceDelay={200}
            onSearchTextChange={setsearchFilterText}
          /> */}
        </>
      }
    >
      <div
        sx={{
          p: '16px',
          bg: sxtheme(t => t.colors.deepSea10),
          minHeight: '100%',
        }}
      >
        {data.hasNewerEntries && (
          <div sx={{ mb: '16px', textAlign: 'center' }}>
            {isLoadingAbove && (
              <Typography variant="subtitle2" color="sea50">
                Loading...
              </Typography>
            )}
            {!isLoadingAbove && (
              <BdButton type="tertiary" onClick={loadAbove} text="Load 10 newer" small />
            )}
          </div>
        )}
        {data.dailyReports.map((entry, iEntry) => (
          <ActivityBlurb
            isGlobal={isWorldwide}
            model={entry}
            isActive={iEntry === activeIndex}
            isScrollToTarget={iEntry === scrollToIndex}
          />
        ))}
        <div sx={{ mt: '16px', textAlign: 'center' }}>
          {data.hasOlderEntries && !isLoadingBelow ? (
            <BdButton type="tertiary" onClick={loadBelow} text="Load 10 more" small />
          ) : data.hasOlderEntries ? (
            <Typography variant="subtitle2" color="sea50">
              Loading...
            </Typography>
          ) : !data.dailyReports?.length ? (
            <Typography variant="body2" color="stone90">
              There is no activity with situational updates
            </Typography>
          ) : (
            <Typography variant="body2" color="stone90" sx={{ textAlign: 'center' }}>
              You've reached the end of the list.
            </Typography>
          )}
        </div>
      </div>
    </Panel>
  );
};
